import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { API_BASE_URL } from "../constant/path";
import axios from "axios";

const PopupForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    requirements: "customised",
    event_type: "Other",
    location: "",
    customLocation: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      location: value,
      customLocation: "",
    }));
    if (errors.location) setErrors((prev) => ({ ...prev, location: "" }));
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (errors.captcha) setErrors((prev) => ({ ...prev, captcha: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Last name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required.";
    if (!formData.location.trim())
      newErrors.location = "Please specify a location.";
    if (formData.location === "Other" && !formData.customLocation.trim())
      newErrors.customLocation = "Please enter a custom location.";
    if (!captchaValue) newErrors.captcha = "Please complete the CAPTCHA.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSubmitted(false);

    try {
      const formattedFormData = {
        ...formData,
        full_name: `${formData.firstName} ${formData.lastName}`.trim(),
        captcha: captchaValue,
        location:
          formData.location === "Other"
            ? formData.customLocation
            : formData.location,
      };

      delete formattedFormData.firstName;
      delete formattedFormData.lastName;
      delete formattedFormData.customLocation;

      const response = await axios.post(
        `${API_BASE_URL}api/contact/`,
        formattedFormData
      );

      if (response?.data?.success) {
        setSubmitted(true);
        onclose();
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          requirements: "customised",
          event_type: "Other",
          location: "",
          customLocation: "",
        });
        setErrors({});
        setCaptchaValue(null);
        setErrorMessage("");
      } else {
        setErrorMessage("Failed to send message. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setSubmitted(false);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-container">
        <div className="modal-header">
          <h3 className="modal-title">Get in Touch</h3>
          <button
            className="close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {submitted && (
            <div className="alert alert-success">
              <div className="alert-content">
                <svg
                  className="alert-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Thank you! Our team will contact you shortly.</span>
              </div>
              <button className="alert-close-btn" onClick={handleDismiss}>
                Close
              </button>
            </div>
          )}

          {errorMessage && (
            <div className="alert alert-error">
              <div className="alert-content">
                <svg
                  className="alert-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  <strong>Error:</strong> {errorMessage}
                </span>
              </div>
            </div>
          )}

          {!submitted && (
            <div className="modal-form">
              {/* Name Fields */}
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    className={`form-input ${errors.firstName ? "error" : ""}`}
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    className={`form-input ${errors.lastName ? "error" : ""}`}
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              {/* Contact Fields */}
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    className={`form-input ${errors.email ? "error" : ""}`}
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>
                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone No."
                    className={`form-input ${errors.phone ? "error" : ""}`}
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && (
                    <span className="error-message">{errors.phone}</span>
                  )}
                </div>
              </div>

              {/* Requirements */}
              <div className="form-group">
                <label className="form-label">Interested in</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="requirements"
                      value="customised"
                      checked={formData.requirements === "customised"}
                      onChange={handleChange}
                    />
                    <span className="radio-label">
                      Customised Personal Tour
                    </span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="requirements"
                      value="group"
                      checked={formData.requirements === "group"}
                      onChange={handleChange}
                    />
                    <span className="radio-label">Personalised Group Tour</span>
                  </label>
                </div>
              </div>

              {/* Location */}
              <div className="form-group">
                <select
                  name="location"
                  className={`form-select ${errors.location ? "error" : ""}`}
                  value={formData.location}
                  onChange={handleLocationChange}
                >
                  <option value="">Select a location</option>
                  <option value="Bali">Bali</option>
                  <option value="Georgia">Georgia</option>
                  <option value="India">India</option>
                  <option value="Goa">Goa</option>
                  <option value="Other">Other</option>
                </select>
                {formData.location === "Other" && (
                  <input
                    type="text"
                    name="customLocation"
                    placeholder="Enter custom location"
                    className={`form-input ${
                      errors.customLocation ? "error" : ""
                    }`}
                    value={formData.customLocation}
                    onChange={handleChange}
                  />
                )}
                {errors.location && (
                  <span className="error-message">{errors.location}</span>
                )}
                {errors.customLocation && (
                  <span className="error-message">{errors.customLocation}</span>
                )}
              </div>

              {/* CAPTCHA */}
              <div className="mb-6">
                <ReCAPTCHA
                  sitekey="6LfG7hsrAAAAABrKlWfFUvwsE5GhzxM7YD6IfhYP"
                  onChange={handleCaptchaChange}
                />
                {errors.captcha && (
                  <p className="text-red-600 text-sm mt-2">{errors.captcha}</p>
                )}
              </div>

              <button
                type="button"
                className="submit-btn"
                disabled={loading}
                onClick={handleSubmit}
              >
                {loading ? (
                  <>
                    <svg
                      className="spinner"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="31.416"
                        strokeDashoffset="31.416"
                      >
                        <animate
                          attributeName="stroke-dashoffset"
                          dur="2s"
                          values="31.416;0"
                          repeatCount="indefinite"
                        />
                      </circle>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideIn 0.3s ease-out;
        }

        .modal-header {
          padding: 24px 24px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .modal-title {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #111827;
        }

        .close-btn {
          background: none;
          border: none;
          padding: 8px;
          cursor: pointer;
          border-radius: 8px;
          color: #6b7280;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .modal-body {
          padding: 0 24px 24px;
        }

        .modal-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        @media (max-width: 640px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-label {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .form-input,
        .form-select {
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          transition: all 0.2s ease;
          background: white;
        }

        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-input.error,
        .form-select.error {
          border-color: #ef4444;
        }

        .error-message {
          color: #ef4444;
          font-size: 14px;
          font-weight: 500;
        }

        .radio-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .radio-option input[type="radio"] {
          width: 18px;
          height: 18px;
          accent-color: #3b82f6;
        }

        .radio-label {
          font-size: 16px;
          color: #374151;
        }

        .mock-captcha {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          background: #f9fafb;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .mock-captcha:hover {
          border-color: #d1d5db;
        }

        .mock-captcha input[type="checkbox"] {
          width: 18px;
          height: 18px;
        }

        .submit-btn {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .alert {
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .alert-success {
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #065f46;
        }

        .alert-error {
          background: #fef2f2;
          border: 1px solid #fca5a5;
          color: #991b1b;
        }

        .alert-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .alert-icon {
          flex-shrink: 0;
        }

        .alert-close-btn {
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        .alert-close-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          .modal-container {
            margin: 10px;
            border-radius: 12px;
          }

          .modal-header {
            padding: 16px 16px 0;
          }

          .modal-body {
            padding: 0 16px 16px;
          }

          .modal-title {
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default PopupForm;
